/**
 * Tenant bootstrap — GET /public/bootstrap/:slug. One call returns the
 * tenant Supabase config, Stripe publishable key, public-safe settings
 * subset, and server-resolved theme tokens (mobile backend-first
 * effort). Replaces the boot path's direct central-DB reads of
 * tenant_public_config in TenantContext and supabaseClient.
 *
 * Distinguishes "no such tenant" (a normal case — typo'd slug,
 * marketing path) from transient errors (timeout, 5xx) so callers can
 * keep their offline-cache fallback and Sentry-noise rules.
 */
import { publicAxios } from "./axiosInstance";

export interface TenantBootstrap {
  tenant: { id?: string | number; slug: string; name: string };
  supabase: { url: string | null; anonKey: string | null };
  stripe: { publishableKey: string | null; disabled: boolean };
  settings: Record<string, any>;
  theme: Record<string, any> | null;
}

export type BootstrapResult =
  | { status: "ok"; data: TenantBootstrap }
  | { status: "not_found" }
  | { status: "error"; error: unknown };

export const fetchTenantBootstrap = async (
  slug: string,
  timeoutMs = 5000
): Promise<BootstrapResult> => {
  try {
    const res = await publicAxios.get(
      `/public/bootstrap/${encodeURIComponent(slug)}`,
      { timeout: timeoutMs }
    );
    const data = res?.data?.data as TenantBootstrap | undefined;
    if (data?.tenant?.slug) return { status: "ok", data };
    return { status: "error", error: new Error("Malformed bootstrap response") };
  } catch (err: any) {
    const httpStatus = err?.response?.status;
    // 404 = unknown/inactive tenant, 400 = garbage slug — both are the
    // expected "no tenant matched" case, not errors.
    if (httpStatus === 404 || httpStatus === 400) return { status: "not_found" };
    return { status: "error", error: err };
  }
};

/**
 * Phase 2 custom domains (PM 01fda321): GET /public/bootstrap-by-host. On a
 * club's own domain the path carries no slug, so the boot sends the hostname
 * and learns the club. The backend answers only for an ACTIVE domain on an
 * active club; an unknown host, a pending or failed domain is not_found (the
 * not-found page, never a default club).
 */
export const fetchTenantBootstrapByHost = async (
  host: string,
  timeoutMs = 5000
): Promise<BootstrapResult> => {
  try {
    const res = await publicAxios.get("/public/bootstrap-by-host", {
      params: { host },
      timeout: timeoutMs,
    });
    const data = res?.data?.data as TenantBootstrap | undefined;
    if (data?.tenant?.slug) return { status: "ok", data };
    return { status: "error", error: new Error("Malformed bootstrap response") };
  } catch (err: any) {
    const httpStatus = err?.response?.status;
    if (httpStatus === 404 || httpStatus === 400) return { status: "not_found" };
    return { status: "error", error: err };
  }
};
